import {
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  Input,
  OnInit,
  Output,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import { WallDecorRenderPreviewConfig } from '../../../../../../../photographer-client/src/app/modules/private-sessions/graphql';
import {
  BehaviorSubject,
  combineLatest,
  from,
  of,
  ReplaySubject,
  timer,
} from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  filter,
  map,
  shareReplay,
  startWith,
  switchMap,
  tap,
} from 'rxjs/operators';
import { BlobStorageService } from 'ZalamoLibrary/src/lib/upload/azure/azure';
import { DomSanitizer } from '@angular/platform-browser';
import { AliveState } from 'ZalamoLibrary/src/lib/core/ActiveState';
import { LookBookProductInBasket } from '../../../../../../../photographer-client/src/app/modules/look-book/graphql';

const cache = new Map<string, any>();

@Component({
    selector: 'lib-wall-decor-preview',
    templateUrl: './wall-decor-preview.component.html',
    styleUrls: ['./wall-decor-preview.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class WallDecorPreviewComponent extends AliveState implements OnInit {
  currentConfig$ = new ReplaySubject<WallDecorRenderPreviewConfig>(1);

  @Output() fakeUpdate = new EventEmitter();
  @Input() @HostBinding('class.calendar') isCalendar = false;
  @Input() set config(config: WallDecorRenderPreviewConfig) {
    this.currentConfig$.next(config);
    this.isLoaded$.next(false);
    this.fakeUpdate.emit();
  }

  // NEW: expose "zoomed" class on host for cursor styling
  @HostBinding('class.zoomed') get zoomedClass() {
    return this.isZoomedIn;
  }

  isLoaded$ = new BehaviorSubject(false);
  isEmpty$ = combineLatest([
    timer(200), // gate: don't emit a real value before 200ms
    combineLatest([this.currentConfig$, this.isLoaded$]).pipe(
      filter(([v]) => !!v && !!v.fileName),
      map(([v, isLoaded]) => v.images.length === 0 || !isLoaded),
    ),
  ]).pipe(
    map(([, real]) => real), // drop timer’s value, keep the real one
    startWith(false), // emit false immediately on subscribe
    distinctUntilChanged(), // avoid duplicate renders
  );

  @Input() set basketValue(value: LookBookProductInBasket) {
    const map = JSON.parse(value.valueMap);
    this.currentConfig$.next({
      wallDecorSceneId: null,
      fileName: value.thumb,
      accessToken: value.accessToken,
      images: Object.values(map),
      wallDecorRenderPreviewConfigId: null,
      color: JSON.parse(value.config)?.color,
    });
  }

  @ViewChild('img1', { static: true }) img1: ElementRef<HTMLElement>;
  @ViewChild('img2', { static: true }) img2: ElementRef<HTMLElement>;
  @ViewChild('container', { static: true }) container: ElementRef<HTMLElement>;

  preview$ = this.currentConfig$.pipe(
    filter((v) => !!v),
    filter(({ fileName }) => !!fileName),
    tap(({ images }) => (this.piecesCount = images.length + 1)),
    switchMap(({ fileName, accessToken }) =>
      cache.has(fileName)
        ? of(cache.get(fileName))
        : from(
            this.blobStorageService
              .downloadFile(
                {
                  storageUri: 'https://newzalamomiddles.blob.core.windows.net',
                  storageAccessToken: accessToken,
                  container: 'zalamo',
                  filename: null,
                },
                'zalamo',
                fileName,
              )
              .then((r) => r.blobBody)
              .then((v) => {
                const { height } =
                  this.elementRef.nativeElement.getBoundingClientRect();
                this.img2.nativeElement.style.transform = `translateY(-${height}px)`;
                return this.domSanitizer.bypassSecurityTrustResourceUrl(
                  URL.createObjectURL(v),
                );
              })
              .then((v) => {
                cache.set(fileName, v);
                return v;
              }),
          ).pipe(catchError((e) => of(null))),
    ),
    tap(() => this.isLoaded$.next(true)),
    shareReplay(1),
  );

  visibleIndex = 0;
  piecesCount = 0;
  mouseOverMe = false;

  // NEW: zoom state + double-tap helpers
  isZoomedIn = false;
  zoomScale = 2; // tweak to taste
  private lastTapTime = 0;
  private lastTapPoint = { x: 0, y: 0 };
  private readonly doubleTapDelay = 280; // ms
  private readonly doubleTapSlop = 30; // px
  private suppressNextClick = false;
  private currentAnimation: Animation | null = null;

  @HostListener('pointerenter') mouseOver() {
    this.mouseOverMe = true;
  }
  @HostListener('pointerleave') mouseLeave() {
    this.mouseOverMe = false;
  }

  // NEW: double‑tap detection (touch/pen/mouse)
  @HostListener('pointerdown', ['$event'])
  onPointerDown(ev: PointerEvent) {
    // ignore non-primary mouse buttons
    if (typeof ev.button === 'number' && ev.button !== 0) return;

    const now = ev.timeStamp;
    const near =
      Math.abs(ev.clientX - this.lastTapPoint.x) < this.doubleTapSlop &&
      Math.abs(ev.clientY - this.lastTapPoint.y) < this.doubleTapSlop;
    const isDouble = near && now - this.lastTapTime < this.doubleTapDelay;

    this.lastTapTime = now;
    this.lastTapPoint = { x: ev.clientX, y: ev.clientY };

    if (isDouble) {
      ev.preventDefault(); // avoid native double-tap zoom on some browsers
      this.suppressNextClick = true;
      this.toggleZoomAt(ev.clientX, ev.clientY);
    }
  }

  // NEW: single tap/click while zoomed toggles back out (nice UX)
  @HostListener('click', ['$event'])
  onClick(ev: MouseEvent) {
    if (this.suppressNextClick) {
      this.suppressNextClick = false;
      return;
    }
    if (this.isZoomedIn) {
      this.toggleZoomAt(ev.clientX, ev.clientY);
    }
  }

  constructor(
    private blobStorageService: BlobStorageService,
    private domSanitizer: DomSanitizer,
    private elementRef: ElementRef<HTMLElement>,
  ) {
    super();
  }

  ngOnInit(): void {
    this.subscribeWhileAlive(
      timer(2000, 2000).pipe(
        // CHANGED: pause auto-advance while zoomed
        filter(() => !this.mouseOverMe && !this.isZoomedIn),
        tap(async () => {
          if (this.visibleIndex === this.piecesCount - 1) {
            this.visibleIndex = 0;
          } else {
            this.visibleIndex++;
          }
          const { height, width } =
            this.elementRef.nativeElement.getBoundingClientRect();

          // CHANGED: keep a handle so we can cancel it when zooming
          const anim = this.container.nativeElement.animate(
            [{ transform: `translateX(-${width}px)` }],
            { duration: 1200 },
          );
          this.currentAnimation = anim;
          try {
            await anim.finished;
          } finally {
            this.currentAnimation = null;
          }

          this.img1.nativeElement.style.transform = `translateY(-${
            height * this.visibleIndex
          }px)`;
          if (this.visibleIndex === this.piecesCount - 1) {
            this.img2.nativeElement.style.transform = `translateY(0px)`;
          } else {
            this.img2.nativeElement.style.transform = `translateY(-${
              height * (this.visibleIndex + 1)
            }px)`;
          }
        }),
      ),
    );
  }

  // NEW: core zoom toggle
  private toggleZoomAt(clientX: number, clientY: number) {
    // Cancel any in-flight slide animation so transforms don’t conflict
    if (this.currentAnimation) {
      try {
        this.currentAnimation.cancel();
      } catch {}
      this.currentAnimation = null;
    }

    const hostRect = this.elementRef.nativeElement.getBoundingClientRect();
    const x = Math.max(0, Math.min(clientX - hostRect.left, hostRect.width));
    const y = Math.max(0, Math.min(clientY - hostRect.top, hostRect.height));

    const el = this.container?.nativeElement;
    if (!el) return;

    el.style.transition = 'transform 200ms ease';

    if (!this.isZoomedIn) {
      // ---- ZOOM IN ----
      el.style.transformOrigin = `${x}px ${y}px`; // pivot at tap point
      el.style.transform = `scale(${this.zoomScale})`;
      this.isZoomedIn = true;
    } else {
      // ---- ZOOM OUT (NO JUMP) ----
      // Do NOT change transformOrigin yet. Animate back to scale(1) using the
      // current pivot, then reset the origin after the transition ends.
      const onEnd = (e: TransitionEvent) => {
        if (e.propertyName === 'transform') {
          el.style.transformOrigin = ''; // reset to default (center) AFTER scale=1
          el.removeEventListener('transitionend', onEnd);
        }
      };
      el.addEventListener('transitionend', onEnd);

      el.style.transform = ''; // animate back to scale(1) with existing origin
      this.isZoomedIn = false;
    }
  }
}
